/**
 * Phase P — Per-section Shipment Request.
 *
 * Wraps the shared GenericShipmentRequest component so each per-section
 * dispatch slug (Color Combo / Art Silk / Kids) gets its own outgoing
 * shipment queue isolated by `from_section` filter. The transfers store
 * lives in the shared `internal_transfers` collection — the dropdown
 * `sectionKey` constraints it to this section's shipments only.
 */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GenericShipmentRequest from '../GenericShipmentRequest';

const SLUG_TO_SECTION_KEY = {
  // Backend's internal-transfer `from_section` lookups match the
  // `section_key` registered in the dispatch sections catalog. The
  // human-readable name is what gets stored on the transfer document.
  color_combo: { key: 'ColorCombo',  name: 'Color Combo Dispatch' },
  art_silk:    { key: 'ArtSilk',     name: 'Art Silk Dispatch' },
  kids:        { key: 'Kids',        name: 'Kids Dispatch' },
};

const PerSectionShipmentRequest = () => {
  const { section: slug } = useParams();
  const navigate = useNavigate();
  const meta = SLUG_TO_SECTION_KEY[slug];

  if (!meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-500 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
        <Button className="mt-4" onClick={() => navigate('/sections/dispatch')} data-testid="back-to-hub-btn">
          Back to Dispatch Hub
        </Button>
      </div>
    );
  }

  return (
    <div data-testid={`per-section-shipment-request-${slug}`}>
      <GenericShipmentRequest
        sectionName={meta.name}
        sectionKey={meta.key}
        backRoute={`/sections/dispatch/${slug}`}
      />
    </div>
  );
};

export default PerSectionShipmentRequest;
